import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { useTRPC } from "@/lib/trpc";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, useRouter } from "@tanstack/react-router";
import type { ZoraEvent } from "@zora/api/concerns/mobile/mobile.router";
import dayjs from "dayjs";
import { ChevronLeft } from "lucide-react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import { ImageInput } from "../image-input/image-input";
import { DateTimePicker } from "../ui/datetime-picker";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "../ui/form";
import { Icons } from "../ui/icons";
import { Textarea } from "../ui/textarea";

// Define the event form schema to include calendarId
const eventFormSchema = z.object({
  title: z.string().min(1, "Title is required"),
  description: z.string(),
  imageUrl: z.string(),
  allDay: z.boolean().default(false),
  start: z.string().datetime(),
  end: z.string().datetime(),
  calendarId: z.string().min(1, "Calendar is required"),
});

type EventFormInput = z.infer<typeof eventFormSchema>;

type EventFormProps =
  | {
      isEdit: true;
      initialValues: ZoraEvent;
    }
  | {
      isEdit: false;
      initialValues?: ZoraEvent;
    };

export function EventsForm({ isEdit, initialValues }: EventFormProps) {
  const router = useRouter();
  const navigate = useNavigate();
  const eventId = initialValues?.id;
  const queryClient = useQueryClient();
  const trpc = useTRPC();

  // Fetch calendars for the dropdown
  const { data: calendars = [], isLoading: isLoadingCalendars } = useQuery(
    trpc.calendars.list.queryOptions(),
  );

  const form = useForm<EventFormInput>({
    resolver: zodResolver(eventFormSchema),
    defaultValues: {
      title: initialValues?.title || "",
      start: initialValues?.start
        ? dayjs(initialValues.start).toISOString()
        : "",
      end: initialValues?.end ? dayjs(initialValues.end).toISOString() : "",
      allDay: initialValues?.allDay || false,
      description: initialValues?.description || "",
      imageUrl: initialValues?.imageUrl || "",
      calendarId: initialValues?.calendarId || "",
    },
  });

  const { mutate: createEventMutation, isPending: isCreatingEvent } =
    useMutation(
      trpc.calendars.createEvent.mutationOptions({
        onSuccess: () => {
          // Invalidate general queries
          queryClient.invalidateQueries({ queryKey: ["events"] });
          queryClient.invalidateQueries({ queryKey: ["calendars"] });

          // Invalidate specific calendar events query that EventsTable uses
          const calendarId = form.getValues("calendarId");
          if (calendarId) {
            queryClient.invalidateQueries({
              queryKey: [["calendars", "events"], { input: { calendarId } }],
            });
          }

          toast.success("Event created successfully");
          navigate({ to: "/calendars" });
        },
        onError: (error) => {
          toast.error(error.message);
        },
      }),
    );

  const { mutate: updateEventMutation, isPending: isUpdatingEvent } =
    useMutation(
      trpc.calendars.updateEvent.mutationOptions({
        onSuccess: () => {
          // Invalidate general queries
          queryClient.invalidateQueries({ queryKey: ["events"] });
          queryClient.invalidateQueries({ queryKey: ["calendars"] });
          if (eventId) {
            queryClient.invalidateQueries({ queryKey: ["event", eventId] });
          }

          // Invalidate specific calendar events query that EventsTable uses
          const calendarId = form.getValues("calendarId");
          if (calendarId) {
            queryClient.invalidateQueries({
              queryKey: [["calendars", "events"], { input: { calendarId } }],
            });
          }

          toast.success("Event updated successfully");
          navigate({ to: "/calendars" });
        },
        onError: (error) => {
          toast.error(error.message);
        },
      }),
    );

  const isSubmitting = isCreatingEvent || isUpdatingEvent;

  const onSubmit = (data: EventFormInput) => {
    if (isEdit && eventId) {
      updateEventMutation({
        ...data,
        id: eventId,
      });
    } else {
      createEventMutation(data);
    }
  };

  return (
    <div className="mx-auto grid max-w-[59rem] flex-1 auto-rows-max gap-4 mt-4">
      <Form {...form}>
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            size="icon"
            className="h-7 w-7"
            onClick={() => {
              router.history.back();
            }}
          >
            <ChevronLeft className="h-4 w-4" />
            <span className="sr-only">Back</span>
          </Button>
          <h1 className="flex-1 shrink-0 whitespace-nowrap text-xl font-semibold tracking-tight sm:grow-0">
            {isEdit ? "Edit Event" : "New Event"}
          </h1>
          <div className="hidden items-center gap-2 md:ml-auto md:flex">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                router.history.back();
              }}
            >
              Discard
            </Button>
            <Button
              size="sm"
              onClick={form.handleSubmit(onSubmit)}
              disabled={isSubmitting}
            >
              {isSubmitting ? <Icons.spinner className="h-4 w-4 mr-2" /> : null}
              Save Event
            </Button>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-[1fr_250px] lg:grid-cols-3 lg:gap-8">
          <div className="grid auto-rows-max items-start gap-4 lg:col-span-2 lg:gap-8">
            <Card>
              <CardHeader>
                <CardTitle>Event</CardTitle>
                <CardDescription>
                  Create or edit an event to be displayed in the app.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid gap-6">
                  <FormField
                    control={form.control}
                    name="title"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Title</FormLabel>
                        <FormControl>
                          <Input
                            autoFocus
                            type="text"
                            className="w-full"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="calendarId"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Calendar</FormLabel>
                        <Select
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                          disabled={isLoadingCalendars}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select a calendar" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {calendars.map((calendar) => (
                              <SelectItem key={calendar.id} value={calendar.id}>
                                {calendar.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                  <div className="grid gap-6">
                    <FormField
                      control={form.control}
                      name="start"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <DateTimePicker
                              label="Start"
                              value={field.value}
                              onChange={field.onChange}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                    <FormField
                      control={form.control}
                      name="end"
                      render={({ field }) => (
                        <FormItem>
                          <FormControl>
                            <DateTimePicker
                              label="End"
                              value={field.value}
                              onChange={field.onChange}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>
                  <FormField
                    control={form.control}
                    name="allDay"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                        <FormControl>
                          <Checkbox
                            checked={field.value}
                            onCheckedChange={field.onChange}
                          />
                        </FormControl>
                        <div className="space-y-1 leading-none">
                          <FormLabel>All Day Event</FormLabel>
                        </div>
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={form.control}
                    name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Description</FormLabel>
                        <FormControl>
                          <Textarea
                            className="w-full min-h-[200px]"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </CardContent>
            </Card>
          </div>
          <div className="grid auto-rows-max items-start gap-4 lg:gap-8">
            <Card className="overflow-hidden">
              <CardHeader>
                <CardTitle>Event Image</CardTitle>
                <CardDescription>
                  Select an image for your event
                </CardDescription>
              </CardHeader>
              <CardContent>
                <FormField
                  name="imageUrl"
                  control={form.control}
                  render={({ field }) => (
                    <ImageInput
                      form={form}
                      field={field}
                      contentType="image/*"
                      displayType="card"
                    />
                  )}
                />
              </CardContent>
            </Card>
          </div>
        </div>
        <div className="flex items-center justify-center gap-2 md:hidden">
          <Button variant="outline" size="sm">
            Discard
          </Button>
          <Button size="sm" onClick={form.handleSubmit(onSubmit)}>
            {isSubmitting ? (
              <Icons.spinner className="mr-2 h-4 w-4 animate-spin" />
            ) : null}
            Save Event
          </Button>
        </div>
      </Form>
    </div>
  );
}
